"use no memo";

import type { z } from "zod";
import { useState } from "react";
import { DialogDescription } from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { EmployeeGroupTypes, SupportedLanguages } from "@repo/database";
import { EmployeeSchema, EmployeeSchemaWithId } from "@repo/validators";
import { Trans, useTranslation } from "react-i18next";

import type { Employee } from "~/providers/employee-provider";
import { useEmployeeGroup } from "~/hooks/use-employee-group";
import { useUser } from "~/hooks/use-user";
import { api } from "~/lib/api";
import { useEmployeeManager } from "~/managers/use-employee-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import { usePermissionStore } from "~/stores/use-permission-store";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { ModalInput } from "../ui/modal-input";
import { MultiSelect } from "../ui/multi-select";

type CreateEmployeeFormData = z.infer<ReturnType<typeof EmployeeSchema>>;
type UpdateEmployeeFormData = z.infer<ReturnType<typeof EmployeeSchemaWithId>>;

export default function EmployeeForm({
  employee,
  onClose,
}: {
  employee?: Employee;
  onClose: (force?: boolean) => void;
}) {
  const { user } = useUser();

  const { employeeGroups } = useEmployeeGroup();

  const { setUnsavedChanges } = useUnsavedChanges();

  const { setIsSettingTag } = usePermissionStore();

  const { updateEmployee, createEmployee } = useEmployeeManager();

  const [passwordLength, setPasswordLength] = useState(
    employee?.pinLength ?? 0,
  );

  const [tagLength, setTagLength] = useState(employee?.tagLength ?? 0);

  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);

  const { t, i18n } = useTranslation("common");

  const form = useForm({
    schema: employee ? EmployeeSchemaWithId(i18n) : EmployeeSchema(i18n),
    defaultValues: {
      id: employee?.id ?? "",
      firstName: employee?.firstName ?? "",
      lastName: employee?.lastName ?? "",
      email: employee?.email ?? "",
      phone: employee?.phoneNumber ?? "",
      pin: "",
      tag: employee?.tag ?? "",
      employeeGroupId: employee?.employeeGroupId ?? "",
      language: employee?.language ?? user.language,
    },
  });

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  setUnsavedChanges({
    isDirty,
    onDiscard() {
      form.reset();
      onClose(true);
    },
  });

  const handleSubmit = form.handleSubmit(async (formData) => {
    if (employee) {
      const { data, error } = await updateEmployee(
        formData as UpdateEmployeeFormData,
      );
      if (data) {
        form.reset();
        onClose(true);
      } else if (error) {
        form.setError("root", { message: error.message });
      }
    } else {
      const { data, error } = await createEmployee(
        formData as CreateEmployeeFormData,
      );
      if (data) {
        form.reset();
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    }
  });

  const { data: pin, isFetching: fetchingPin } = api.employee.getCode.useQuery(
    employee?.id,
    {
      enabled: pinDialogOpen && !!employee?.id,
    },
  );

  const { data: tag, isFetching: fetchingTag } = api.employee.getTag.useQuery(
    employee?.id,
    {
      enabled: tagDialogOpen && !!employee?.id,
    },
  );

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        id="employee-form"
        className="grid grid-cols-2 gap-2"
      >
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Employee.first_name")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t("Employee.first_name")} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Employee.last_name")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t("Employee.last_name")} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="pin"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Employee.pin")}</FormLabel>
              <FormControl>
                <ModalInput
                  type="password"
                  passwordLength={passwordLength}
                  onChange={(value) => {
                    field.onChange(value);
                    setPasswordLength(value.length);
                  }}
                />
              </FormControl>
              {employee && (
                <FormDescription>
                  <Trans
                    i18nKey="Employee.pin_description"
                    components={{
                      reveal: (
                        <span
                          className="cursor-pointer text-foreground"
                          onClick={() => setPinDialogOpen(true)}
                        ></span>
                      ),
                    }}
                  ></Trans>
                </FormDescription>
              )}

              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="tag"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Employee.tag")}</FormLabel>
              <FormControl>
                <ModalInput
                  onClick={() => setIsSettingTag(true)}
                  onClose={() => setIsSettingTag(false)}
                  type="password"
                  passwordLength={tagLength}
                  onChange={(value) => {
                    field.onChange(value);
                    setTagLength(value.length);
                  }}
                />
              </FormControl>
              {employee && (
                <FormDescription>
                  <Trans
                    i18nKey="Employee.tag_description"
                    components={{
                      reveal: (
                        <span
                          className="cursor-pointer text-foreground"
                          onClick={() => setTagDialogOpen(true)}
                        ></span>
                      ),
                    }}
                  ></Trans>
                </FormDescription>
              )}

              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="language"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Employee.language")}</FormLabel>
              <FormControl>
                <MultiSelect
                  maxSelect={1}
                  placeholder="Select a language..."
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                    void form.trigger();
                  }}
                  options={Object.values(SupportedLanguages)
                    .sort()
                    .map((lang) => ({
                      label: lang,
                      value: lang,
                    }))}
                  defaultValue={[field.value]}
                  value={[field.value]}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="employeeGroupId"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Employee.group")}</FormLabel>
              <FormControl>
                <MultiSelect
                  maxSelect={1}
                  placeholder="Select a group..."
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                    void form.trigger();
                  }}
                  options={employeeGroups
                    .filter(
                      (group) => group.type !== EmployeeGroupTypes.default,
                    )
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((employeeGroup) => ({
                      label: employeeGroup.name,
                      value: employeeGroup.id,
                    }))}
                  defaultValue={field.value ? [field.value] : []}
                  value={field.value ? [field.value] : []}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {form.formState.errors.root && (
          <FormMessage className="col-span-full">
            {form.formState.errors.root.message}
          </FormMessage>
        )}
      </form>
      <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
        <DialogContent>
          <VisuallyHidden>
            <DialogHeader>
              <DialogTitle></DialogTitle>
              <DialogDescription></DialogDescription>
            </DialogHeader>
          </VisuallyHidden>
          <div className="min-h-5 p-4">
            <p className="text-center">
              {fetchingPin ? "Retrieving PIN..." : pin}
            </p>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
        <DialogContent>
          <VisuallyHidden>
            <DialogHeader>
              <DialogTitle></DialogTitle>
              <DialogDescription></DialogDescription>
            </DialogHeader>
          </VisuallyHidden>
          <div className="min-h-5 p-4">
            <p className="text-center">
              {fetchingTag ? "Retrieving TAG..." : tag}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </Form>
  );
}
